import { Injectable } from '@angular/core';
import { SLUG_CONFIG } from '../constants/fhir.constants';
import { ILinkIdGenerator } from '../interfaces/fhir-converter.interfaces';

/**
 * LinkId Generator Service
 *
 * Responsible for generating unique identifiers for FHIR Questionnaire items. Provides consistent linkId generation
 * across the conversion pipeline.
 *
 * Features:
 *
 * - 3-character unique slugs for compactness
 * - UUID v4 generation for template/section IDs
 * - Hierarchical linkId generation for nested structures
 */
@Injectable({
  providedIn: 'root',
})
export class LinkIdGenerator implements ILinkIdGenerator {
  /** Generate a unique 3-character slug Uses alphanumeric characters for URL-safe identifiers */
  generateSlug(): string {
    const { CHARS, LENGTH } = SLUG_CONFIG;
    let slug = '';
    for (let i = 0; i < LENGTH; i++) {
      slug += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
    }
    return slug;
  }

  /**
   * Generate a linkId from title with unique suffix Converts title to URL-safe slug and appends unique identifier
   *
   * @param title - Human-readable title to convert
   * @returns Slugified title with unique suffix (e.g., "patient-info-a2x")
   */
  generateFromTitle(title: string): string {
    const baseSlug = this.slugify(title);
    const uniqueSlug = this.generateSlug();
    return `${baseSlug}-${uniqueSlug}`;
  }

  /** Generate a UUID v4 Used for template and section IDs where uniqueness is critical */
  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Generate control linkId based on type and parent path Creates hierarchical linkIds for proper FHIR structure
   *
   * @param controlType - The control type (e.g., 'text-input', 'checkbox')
   * @param parentPath - Parent linkId for nesting
   * @param isLayout - Whether this is a layout control (section/subsection)
   * @param displayText - Optional display text for layout controls
   * @returns Hierarchical linkId (e.g., "section-abc.textinput-xyz")
   */
  generateControlLinkId(controlType: string, parentPath: string, isLayout: boolean, displayText?: string): string {
    const uniqueSlug = this.generateSlug();
    // Use only the immediate parent segment (last part after the last dot), not the full hierarchical path
    const immediateParent = parentPath ? parentPath.split('.').pop()! : '';

    if (isLayout) {
      // For layout controls, use display text as base
      const baseName = this.slugify(displayText || `item-${Date.now()}`);
      return immediateParent ? `${immediateParent}.${baseName}-${uniqueSlug}` : `${baseName}-${uniqueSlug}`;
    }

    // For regular controls, use control type
    const sanitizedType = controlType.toLowerCase().replace(/[^a-z0-9]+/g, '');
    return immediateParent ? `${immediateParent}.${sanitizedType}-${uniqueSlug}` : `${sanitizedType}-${uniqueSlug}`;
  }

  /**
   * Generate a hierarchical linkId for subsections
   *
   * @param baseName - Base name for the subsection
   * @param parentLinkId - Parent section's linkId
   * @returns Combined linkId (e.g., "section-abc.subsection-xyz")
   */
  generateSubsectionLinkId(baseName: string, parentLinkId: string): string {
    const slugifiedBase = this.slugify(baseName);
    // Use only the immediate parent segment, not the full hierarchical path
    const immediateParent = parentLinkId ? parentLinkId.split('.').pop()! : '';
    return immediateParent ? `${immediateParent}.${slugifiedBase}` : slugifiedBase;
  }

  /**
   * Convert text to URL-safe slug
   *
   * @param text - Text to convert
   * @returns URL-safe slug
   */
  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
